import type { DeliveryMethodSettingValue } from "services/KnowfullyAdminClient";
import type { SkuDeliveryMethodSettingProps } from "./SkuDeliveryMethodSetting";

const bySequence = (left?: number, right?: number) => (left ?? 0) - (right ?? 0);

/**
 * The settings the form offers, in order, each carrying the array slot its fields bind to.
 *
 * Only the overridable ones. A setting the delivery method declares as not overridable is one a
 * deliverable may not hold its own value for, so a field for it is a field nobody is allowed to
 * fill: KONE-25020 declares the frame an embed is drawn at that way, because the size belongs to
 * the delivery method rather than to the person pasting the markup.
 *
 * The slot has to be the position in this list rather than the setting's own sequence: the form
 * state is an array, the values that reach the server are that array, and a field whose path points
 * at a slot it does not own writes over its neighbour. These methods had a single setting until the
 * frame settings arrived, which is why a sequence worked as an index for as long as it did.
 */
export const settingsToRender = (
  settings: SkuDeliveryMethodSettingProps[] | undefined
): SkuDeliveryMethodSettingProps[] =>
  [...(settings ?? [])]
    .filter((setting) => setting.overridable)
    .sort((left, right) => bySequence(left.sequence, right.sequence))
    .map((setting, index) => ({ ...setting, fieldIndex: index }));

/** The stored values behind that same list, so slot N holds the value of the field at slot N. */
export const settingValuesToBind = (
  settingValues: DeliveryMethodSettingValue[] | undefined
): DeliveryMethodSettingValue[] | undefined =>
  settingValues
    ?.filter((settingValue) => settingValue.setting?.overridable)
    .sort((left, right) => bySequence(left.setting?.sequence, right.setting?.sequence));
